import {Injectable} from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient, HttpResponse} from '@angular/common/http';
import {SubmissionRecordObject, SampleSearchRecordObject, SampleDetailsRecordObject, TestRecordDataObject
} from './submission-history-object';
import {
    SubmissionSearchObject,
    SampleSearchObject,
    SubmissionSearchUserInfoObject
} from '../../components/dashboard/search-object';
import {BehaviorSubject} from 'rxjs';
import {Extract} from './extract-service';
import {PaginationObject} from '../../components/dashboard/pagination-object';
import { ErrorDetails } from '../../components/dashboard/errors/errordetails';
import { createHttpParams } from './http-params';

@Injectable()
export class SubmissionHistoryService {

    private submissionTable = new BehaviorSubject<SubmissionRecordObject[]>(null);
    currentSubmissionTable = this.submissionTable.asObservable();

    private sampleSearchTable = new BehaviorSubject<SampleSearchRecordObject[]>(null);
    currentSampleSearchTable = this.sampleSearchTable.asObservable();

    private sampleTable = new BehaviorSubject<SampleSearchRecordObject[]>(null);
    currentSampleTable = this.sampleTable.asObservable();

    private sampleSearchCriteria = new BehaviorSubject<SampleSearchObject>(null);
    currentSampleSearchCriteria = this.sampleSearchCriteria.asObservable();

    private sampleSubmissionData = new BehaviorSubject<SubmissionRecordObject>(null);
    currentSampleSubmissionData = this.sampleSubmissionData.asObservable();

    private userInfo = new BehaviorSubject<SubmissionSearchUserInfoObject>(null);
    currentUserInfo = this.userInfo.asObservable();


    private searchCriteria = new BehaviorSubject<SubmissionSearchObject>(null);
    currentSearchCriteria = this.searchCriteria.asObservable();

    private totalCount = new BehaviorSubject<number>(null);
    currentTotalCount = this.totalCount.asObservable();

    private pagination = new BehaviorSubject<PaginationObject>(null);
    currentPagination = this.pagination.asObservable();

    private samplePagination = new BehaviorSubject<PaginationObject>(null);
    currentSamplePagination = this.samplePagination.asObservable();

    private errorDetails = new BehaviorSubject<ErrorDetails[]>(null);
    currentErrorDetails = this.errorDetails.asObservable();

    private sampleErrorDetails = new BehaviorSubject<ErrorDetails[]>(null);
    currentSampleErrorDetails = this.sampleErrorDetails.asObservable();

    private sampleDetailsTable = new BehaviorSubject<SampleDetailsRecordObject[]>(null);
    currentSampleDetailsTable = this.sampleDetailsTable.asObservable();

    private testSubmissionData = new BehaviorSubject<TestRecordDataObject>(null);
    currentTestSubmissionData = this.testSubmissionData.asObservable();

    constructor(private http: HttpClient) {
        this.pagination.next(this.buildPagination(100));
        this.samplePagination.next(this.buildPagination(500));
        const searchObjectInit = new SubmissionSearchObject;
        searchObjectInit.submissionIdentifierType = 'hml';
        this.changeSearchCriteria(searchObjectInit);
        this.changeSampleSearchCriteria(new SampleSearchObject());
    }

    // Clears data from the UI on logout so that the next user doesn't accidentally see it
    public clearUserData(): void {
        // Note: At this point only a subset of the user data is cleared on logout. Other data might
        // need to be cleared in the future based on observed behavior and/or security requirements.
        this.changeMessage(null);
        this.clearSubmissionPagination(0);
        this.changeUserInfo(new SubmissionSearchUserInfoObject());
        this.changeSearchCriteria(new SubmissionSearchObject());
        this.changeSampleSearchCriteria(new SampleSearchObject());
    }

    // Reload on login (reverse the effect of clearUserData)
    public reloadUserData(): void {
        this.getUserSearchInfo().then();
    }

    getSessionTableContent() {
        const url = `/dashboard/submissions`;

        const pc: PaginationObject = this.pagination.getValue();

        return this.http.get(url, {
            params: new HttpParams()
                .set('page', pc.currentPage.toString())
                .set('results-per-page', pc.pageSize.toString())
            })
            .toPromise()
            .then(resp => this.changeMessage(this.toFileRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSampleSubmissionData(uuid: string) {
        const url = `/dashboard/sampleSubmissionData/${uuid}`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.changeSampleSubmissionData(this.toFileRecordObject(resp)))
            .catch(this.handleError);
    }

    getTestSubmissionData(uuid: string, sampleId: string) {
        const url = `/dashboard/sampleSubmissionData/${uuid}/testSubmissionData/${sampleId}`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.changeTestSubmissionData(this.toTestRecordDataObject(resp)))
            .catch(this.handleError);
    }

    getSessionTableExportContent() {
        const url = `/dashboard/export/submissions`;
        const pc: PaginationObject = this.pagination.getValue();
        return this.http.get(url, {
            params: new HttpParams()
                .set('results-per-page', `${pc.totalCount}`), responseType: 'text'
            })
            .toPromise()
            .then(function (resp) {
                // new Extract().specialMessages(resp);
                new Extract().toCsv(resp, 'submission-history.csv')
            })
            .catch(this.handleError);
    }

    getSubmissionSamplesExportContent( uuid: string ) {

        var todayString = this.getExtractFileName();

        const url = `/dashboard/export/submissionSamples`;
        return this.http.get(url, {
            params: new HttpParams()
            .set('uuid', `${uuid}`), responseType: 'text'
            })
            .toPromise()
            .then(function (resp) {
                new Extract().toCsv(resp, 'samples_' + uuid + '_' + todayString + '.csv')
            })
            .catch(this.handleError);
    }

    getSamplesSearchExportContent() {

        if ( this.sampleSearchTable.value === null ) {
            return;
        }
        let params: HttpParams = createHttpParams(this.sampleSearchCriteria.value);

        const startDateTime = (<HTMLInputElement>$('#startDate')[0]).value + ' ' + (<HTMLInputElement>$('#startTime')[0]).value;
        const   endDateTime = (<HTMLInputElement>$(  '#endDate')[0]).value + ' ' + (<HTMLInputElement>$(  '#endTime')[0]).value;

        if ( startDateTime.trim() !== '' ) {
            params = params.set('startDate', startDateTime)
        }
        if ( endDateTime.trim() !== '' ) {
            params = params.set('endDate', endDateTime)
        }

        var todayString = this.getExtractFileName();

        const url = `/dashboard/export/sampleSearch`;
        return this.http.get(url, {params, responseType: 'text'})
            .toPromise()
            .then(function (resp) {
                new Extract().toCsv(resp, 'samples_search_' + todayString + '.csv')
            })
            .catch(this.handleError);
    }

    getExtractFileName() {
        var today = new Date();
        var dd = today.getDate();
        var mm = today.getMonth() + 1; // January is 0!
        var yyyy = today.getFullYear();
        return mm + '_' + dd + '_' + yyyy;
    }


    getTotalCount(): Promise<any> {
        const url = `/dashboard/submissions/total`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.updatePagination(new Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getUserSearchInfo() {
        const url = `/dashboard/dashboardUserInfo`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.changeUserInfo(resp))
            .catch(this.handleError);
    }

    getSearchTableContent(search: SubmissionSearchObject, startDate: string, endDate: string, page: number) {
        const url = `/dashboard/submissionSearch`;
        this.changeSearchCriteria(search);
        const pc: PaginationObject = this.pagination.getValue();

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('results-per-page', `${pc.pageSize}`)

        params = params.set('page', page.toString())

        return this.http.get(url, {params})
            .toPromise()
            .then(resp => this.changeMessage(this.toFileRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSearchTableExportContent(search: SubmissionSearchObject, startDate: string, endDate: string, page: number) {
        const url = `/dashboard/export/submissionSearch`;
        const pc: PaginationObject = this.pagination.getValue();

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('results-per-page', `${pc.pageSize}`)

        return this.http.get(url, {params, responseType: 'text'})
            .toPromise()
            .then(function (resp) {
                // new Extract().specialMessages(resp);
                new Extract().toCsv(resp, 'submission-history.csv')
            })
            .catch(this.handleError);
    }

    getSearchTableContentCount(search: SubmissionSearchObject, startDate: string, endDate: string) {
        const url = `/dashboard/submissionSearch/total`;
        this.changeSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }

        return this.http.get(url, {params})
            .toPromise()
            .then(resp => this.updatePagination(new Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getSampleTableContent(uuid: string) {
        const url = `/dashboard/samples`;
        return this.http
            .get(url, { params: { docid_guid: uuid } })
            .toPromise()
            .then(resp =>
                this.changeSampleTable(
                    this.toSampleSearchRecordObjectList(resp)
                )
            )
            .catch(this.handleError);
    }

    getSampleSearchTableContent(search: SampleSearchObject, startDate: string, endDate: string, page: number) {
        const url = `/dashboard/sampleSearch`;
        this.changeSampleSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('page', page.toString())

        return this.http.get(url, {params})
            .toPromise()
            .then(resp => this.changeSampleSearchTable(this.toSampleSearchRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSampleSearchTableContentCount(search: SampleSearchObject, startDate: string, endDate: string) {
        const url = `/dashboard/sampleSearch/total`;
        this.changeSampleSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }

        return this.http.get(url, {params})
            .toPromise()
            .then(resp => this.updateSamplePagination(new Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getSampleDetailsTableContent(uuid: string, sampleId: string) {
        const url = `/dashboard/submissions/${uuid}/samples/${sampleId}`;
        return this.http
            .get(url, {})
            .toPromise()
            .then(resp =>
                this.changeSampleDetailsTable(
                    this.toSampleDetailsRecordObjectList(resp)
                )
            )
            .catch(this.handleError);
    }


    getErrors(uuid: any): Promise<ErrorDetails[]> {
        const url = `/dashboard/submissions/${uuid}/errors`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.changeErrorDetails(this.toErrorDetailsObjectList(resp)))
            .catch(this.handleError);
    }


    getSampleErrors(uuid: string, sampleId: string): Promise<ErrorDetails[]> {
        const url = `/dashboard/submissions/${uuid}/samples/${sampleId}/errors`;
        return this.http.get(url, {})
            .toPromise()
            .then(resp => this.changeSampleErrorDetails(this.toErrorDetailsObjectList(resp)))
            .catch(this.handleError);
    }

    changeErrorDetails(errors: ErrorDetails[]) {
        this.errorDetails.next(errors);
    }


    changeSampleErrorDetails(errors: ErrorDetails[]) {
        this.sampleErrorDetails.next(errors);
    }


    toErrorDetailsObjectList(jsonErrorList: any): ErrorDetails[] {
        const errorDetailsList: ErrorDetails[] = new Array<ErrorDetails>();
        for (const error of jsonErrorList) {
            errorDetailsList.push(error);
        }
        return errorDetailsList;
    }


    resolve(checked: boolean, uuid: any): Promise<any> {
        const url = `/dashboard/submissions/${uuid}/setResolution/` + checked;
        return this.http.put(url, {})
            .toPromise()
            .then(resp => resp)
            .catch(this.handleError);
    }

    review(checked: boolean, uuid: any): Promise<any> {
        const url = `/dashboard/submissions/${uuid}/setTCReview/` + checked;
        return this.http.put(url, {})
            .toPromise()
            .then(resp => resp)
            .catch(this.handleError);
    }

    sampleSearchReview(checked: boolean, docidGuid: any, sampleId: any): Promise<any> {
        const url = `/dashboard/submissions/${docidGuid}/${sampleId}/setSampleSearchReview/` + checked;
        return this.http.put(url, {})
            .toPromise()
            .then(resp => resp)
            .catch(this.handleError);
    }

    getSubmissionText(uuid: string): Promise<HttpResponse<string>> {
        let url = `/dashboard/submissions/${uuid}/source`;
        return this.http.get(url, {observe: 'response', responseType: 'text'})
            .toPromise()
            .catch(this.handleError);
    }


    getSampleSource(uuid: string, sampleId: string) {
        let url = `/dashboard/submissions/${uuid}/samples/${sampleId}/source`;
        return this.http.get(url, {responseType: 'text'})
            .toPromise()
            .catch(this.handleError);
    }

    getFhirSampleSource(uuid: string, sampleId: string) {
        let url = `/dashboard/submissions/${uuid}/samples/${sampleId}/fhirsource`;
        return this.http.get(url, {responseType: 'text'})
            .toPromise()
            .catch(this.handleError);
    }

    resubmitSubmission(submissionXml: string): Promise<HttpResponse<string>> {
        const httpOptions = {
            headers: new HttpHeaders({
              'Content-Type':  'text/plain' //<- To SEND XML
            })
          };
          return this.http.post('/dashboard/resubmit/submit', submissionXml, httpOptions)
          .toPromise()
          .catch(this.handleError)
    }

    reprocessSubmission(uuid: string): Promise<HttpResponse<string>> {
        const httpOptions = {
            headers: new HttpHeaders({
              'Content-Type':  'text/plain' //<- To SEND XML
            })
          };
          return this.http.post('/dashboard/resubmit/reprocess', uuid, httpOptions)
          .toPromise()
          .catch(this.handleError)
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }

    toFileRecordObjectList(jsonFileRecordList: any): SubmissionRecordObject[] {
        const fileRecordObjectList: SubmissionRecordObject[] = new Array<SubmissionRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }

    toFileRecordObject(jsonFileRecord: any): SubmissionRecordObject {
        return jsonFileRecord;
    }

    toSampleSearchRecordObjectList(jsonFileRecordList: any): SampleSearchRecordObject[] {
        const fileRecordObjectList: SampleSearchRecordObject[] = new Array<SampleSearchRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }


    toSampleDetailsRecordObjectList(jsonFileRecordList: any): SampleDetailsRecordObject[] {
        const fileRecordObjectList: SampleDetailsRecordObject[] = new Array<SampleDetailsRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }

    toTestRecordDataObject(jsonFileRecordList: any): TestRecordDataObject {
        return jsonFileRecordList;
    }

    changeMessage(message: SubmissionRecordObject[]) {
        this.submissionTable.next(message);
    }

    changeSampleSearchTable(message: SampleSearchRecordObject[]) {
        this.sampleSearchTable.next(message);
    }

    clearSampleSearchTable() {
        this.sampleSearchTable.next(null);
    }

    changeSampleTable(message: SampleSearchRecordObject[]) {
        this.sampleTable.next(message);
    }

    clearSampleTable() {
        this.sampleTable.next(null);
    }

    changeSampleDetailsTable(message: SampleDetailsRecordObject[]) {
        this.sampleDetailsTable.next(message);
    }

    clearSampleDetailsTable() {
        this.sampleDetailsTable.next(null);
    }

    changeSampleSubmissionData(message: SubmissionRecordObject) {
        this.sampleSubmissionData.next(message);
    }

    clearSampleSubmissionData() {
        this.sampleSubmissionData.next(null);
    }

    changeTestSubmissionData(message: TestRecordDataObject) {
        this.testSubmissionData.next(message);
    }

    clearTestSubmissionData() {
        this.testSubmissionData.next(null);
    }

    changeUserInfo(message: SubmissionSearchUserInfoObject) {
        // TODO : Don't hard code on web-tier (okay with business in the short term)
        message.loci = ['HLA-A', 'HLA-B', 'HLA-C', 'HLA-DRB1', 'HLA-DQB1',
                        'HLA-DRB3', 'HLA-DRB4', 'HLA-DRB5', 'HLA-DPB1', 'HLA-DQA1', 'HLA-DPA1'];
        this.userInfo.next(message);
    }

    changeSearchCriteria(message: SubmissionSearchObject) {
        this.searchCriteria.next(message);
    }

    changeSampleSearchCriteria(message: SampleSearchObject) {
        this.sampleSearchCriteria.next(message);
    }

    clearSampleSearchCriteria() {
        this.sampleSearchCriteria.next(new SampleSearchObject);
    }

    changePagination(message: PaginationObject) {
        this.pagination.next(message);
    }

    updatePagination(totalCount: number) {
        const pc: PaginationObject = this.pagination.getValue();
        pc.totalCount = totalCount;
        this.changePagination(pc);
    }

    changeSamplePagination(message: PaginationObject) {
        this.samplePagination.next(message);
    }

    updateSamplePagination(totalCount: number) {
        const pc: PaginationObject = this.samplePagination.getValue();
        pc.totalCount = totalCount;
        this.changeSamplePagination(pc);
    }

    clearSamplePagination(totalCount: number) {
        const pc: PaginationObject = this.samplePagination.getValue();
        pc.currentPage = 0;
        pc.totalCount = totalCount;
        this.changeSamplePagination(pc);
    }

    clearSubmissionPagination(totalCount: number) {
        const pc: PaginationObject = this.pagination.getValue();
        pc.currentPage = 0;
        pc.totalCount = totalCount;
        this.changePagination(pc);
    }

    buildPagination(pageSize: number) {
        const pagination = new PaginationObject();
        pagination.currentPage = 0;
        pagination.pageSize = pageSize;
        return pagination;
    }

    public getNotes(uuid: string){
        let url = `/dashboard/submissions/${uuid}/notes`;
        return this.http.get(url, {responseType: 'text'})
            .toPromise()
            .catch(this.handleError);
    }


    public saveNotes(uuid: string, noteText: string): Promise<Response> {
        let url = `/dashboard/submissions/${uuid}/notes`;
        return this.http.put(url, noteText, {headers: new HttpHeaders()
                .set('Content-Type', 'text/plain')
            })
            .toPromise()
            .catch(this.handleError);
    }
}
